/**
 * Centralized cluster color palette — single source of truth for all scenes.
 *
 * Color logic:
 *   Woodlands    → wide-range greens (dark forest → bright lime → pale sage)
 *   Jesse Thicket → burnt amber
 *   Miombo       → deep purple (breaks green monotony, still ecological)
 *   Riverine     → dark teal
 *   Bushland     → amber yellow
 *   Mixed        → warm tan
 *
 * No red (reserved for anomaly/warning states).
 */

/** Cluster colors indexed by cluster id (matches clusters.json order). */
export const CLUSTER_COLORS: readonly string[] = [
  '#2A7A32',  // 0 Mopane Woodland       — dark forest green
  '#7DC84A',  // 1 Combretum Woodland    — bright lime green
  '#B07030',  // 2 Jesse Thicket         — burnt amber
  '#6048C0',  // 3 Miombo Woodland       — deep purple
  '#A8CC7A',  // 4 Terminalia Woodland   — pale sage green
  '#1A9898',  // 5 Riverine              — dark teal
  '#D4A820',  // 6 Bushland              — amber yellow
  '#A89068',  // 7 Mixed Woodland        — warm tan
];

/** Return color for a cluster by its id. */
export function clusterColorById(id: number): string {
  return CLUSTER_COLORS[id] ?? CLUSTER_COLORS[0];
}

/**
 * Build a species-name → cluster-id map from the loaded clusters array.
 * Call once after clusters.json is fetched.
 */
let _nameToId: Map<string, number> = new Map();
let _initialized = false;

export function initClusterLookup(clusters: { id: number; species: { name: string }[] }[]): void {
  _nameToId = new Map();
  clusters.forEach(c => c.species.forEach(sp => _nameToId.set(sp.name, c.id)));
  _initialized = true;
}

/** Return cluster color for a species by name, or null if not found. */
export function clusterColorByName(name: string): string | null {
  if (!_initialized) return null;
  const id = _nameToId.get(name);
  return id !== undefined ? CLUSTER_COLORS[id] : null;
}

/** Return cluster id for a species by name, or -1 if not found. */
export function clusterIdByName(name: string): number {
  return _nameToId.get(name) ?? -1;
}
